import { useMemo } from "react";

import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { resolveCrmOrganizationId } from "src-next/modules/crm-clinical/communications/helpers/resolveCrmOrganizationId";
import { useMessageTemplates } from "src-next/modules/crm-clinical/messageTemplates/hooks/useMessageTemplates";
import { useWhatsAppEndpoint } from "src-next/modules/crm-clinical/messaging/hooks/useWhatsAppEndpoint";

import {
  buildPostAttendanceFollowUpPreview,
  type PostAttendanceFollowUpPreviewContent,
} from "../helpers/buildPostAttendanceFollowUpPreview";
import type {
  PostAttendanceFollowUpType,
  PostAttendancePatientData,
} from "./usePostAttendanceFollowUpSendDialog";

export interface UsePostAttendanceFollowUpPreviewParams {
  open: boolean;
  type: PostAttendanceFollowUpType;
  data: any;
  patientData?: PostAttendancePatientData;
}

export interface UsePostAttendanceFollowUpPreviewResult {
  preview: PostAttendanceFollowUpPreviewContent;
  isLoading: boolean;
}

/**
 * Monta a prévia do FollowUpModal a partir do template WhatsApp (`flow_medication` / `flow_exame`).
 */
export function usePostAttendanceFollowUpPreview({
  open,
  type,
  data,
  patientData,
}: UsePostAttendanceFollowUpPreviewParams): UsePostAttendanceFollowUpPreviewResult {
  const { access } = useSessionContext();
  const { organizationId: contextOrganizationId, organization } =
    useOrganizationContext();

  const effectiveOrganizationId = useMemo(
    () =>
      resolveCrmOrganizationId(
        contextOrganizationId,
        access?.organizationId,
      ),
    [contextOrganizationId, access?.organizationId],
  );

  const { wabaIdRank1, isLoading: isEndpointLoading } = useWhatsAppEndpoint({
    organizationId: effectiveOrganizationId,
  });

  const { templates, isLoading: isTemplatesLoading } = useMessageTemplates({
    organizationId: effectiveOrganizationId,
    whatsappBusinessAccountId: wabaIdRank1 ?? undefined,
    enabled: open && !isEndpointLoading,
  });

  const organizationName =
    organization?.name?.[0]?.text?.trim() ||
    organization?.alias?.[0]?.trim() ||
    "";

  const preview = useMemo(
    () =>
      buildPostAttendanceFollowUpPreview({
        type,
        data,
        patientData,
        organizationName,
        templates,
      }),
    [data, organizationName, patientData, templates, type],
  );

  return {
    preview,
    isLoading: open && (isEndpointLoading || isTemplatesLoading),
  };
}
